import * as React from 'react'
import ReactQuill from 'react-quill'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { adminService } from '../../../../core/services'
import { BREAKPOINT_TABLET_SMALL, THEME_COLOR_VARIABLE_NAME } from '../../../../static/constants'
import { DropZoneUploader } from '../../../atoms'
import { CheckBox, InputText, Select, Spacer } from '../../../atoms'
import { InputDateTime } from '../../../molecules'
import AdminLayoutEdit from '../_layouts/edit'

interface IProps {
  info: IJsonResponse
}
interface IImage {
  image_url: string
  file?: any
}

const FIELDS = {
  id: 'id',
  title: 'title',
  content: 'content',
  open_reservation_at: 'open_reservation_at',
  close_reservation_at: 'close_reservation_at',
  opened: 'opened',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('info', { scope: 'activerecord.models' }),
})

const AdminInfoEdit: React.FC<IProps> = props => {
  const { data: info } = adminService.getDataFromJson(props.info)

  const [image, setImage] = React.useState<IImage>(
    info.info_image && info.info_image.image_url
      ? {
          image_url: info.info_image.image_url,
        }
      : null
  )
  const [storeOpenedChecked, setOpenedChecked] = React.useState(info.opened)
  const [quillContent, setQuillContent] = React.useState(info.content)

  const handleSubmit = React.useCallback(async (initialValues, values) => {
    const { updatedInfo, flush } = await adminService.updateInfo(values, imageToUpload.current)
    location.href = '/admin/infos'
  }, [])

  const onDropHandler = addedImages => {
    addedImages.forEach(addedImage => {
      const fileReader = new FileReader()
      fileReader.onloadend = (event: any) => {
        setImage({
          file: addedImage,
          image_url: event.target.result,
        })
      }
      fileReader.readAsDataURL(addedImage)
    })
  }

  const handleFileSelect = event => {
    const newImage = event.target.files[0]
    const fileReader = new FileReader()
    fileReader.onloadend = (eventRoader: any) => {
      setImage({
        file: newImage,
        image_url: eventRoader.target.result,
      })
    }
    fileReader.readAsDataURL(newImage)
  }

  const imageToUpload = React.useRef(image)
  React.useEffect(() => {
    imageToUpload.current = image
  }, [image])

  const onChangeQuill = (html, delta, source, editor) => {
    setQuillContent(editor.getHTML())
  }

  return (
    <AdminLayoutEdit
      model="info"
      title={title}
      fields={FIELDS}
      indexLink="/admin/infos"
      formItems={
        <>
          <input name="id" defaultValue={info.id} type="hidden" />
          <InputText
            required={true}
            name="title"
            defaultValue={info.title}
            label={I18n.t('generic.title')}
          />
          <Spacer />
          <S.FormItem>
            <S.ImageWrapper>
              <S.FormItemTitle>{I18n.t('generic.category_image')}</S.FormItemTitle>
              {image && (
                <S.PreviewImage>
                  {image.image_url === '/images/no-avatar.svg' ? (
                    <p>画像がありません</p>
                  ) : (
                    <S.ItemImage>
                      <img src={image.image_url} />
                    </S.ItemImage>
                  )}
                  <S.ImageButtons>
                    <S.FileSelectWrapper>
                      <label htmlFor="fileSelectUploader">
                        {I18n.t('generic.change')}
                        <input id="fileSelectUploader" type="file" onChange={handleFileSelect} />
                      </label>
                    </S.FileSelectWrapper>
                  </S.ImageButtons>
                </S.PreviewImage>
              )}
              <S.DropZoneWrapper hasImage={image ? true : false}>
                <DropZoneUploader onDrop={onDropHandler} />
              </S.DropZoneWrapper>
            </S.ImageWrapper>
          </S.FormItem>
          <Spacer />
          <S.FormItem>
            <S.FormItemTitle>お知らせ本文</S.FormItemTitle>
            <Spacer />
            <input type="hidden" value={quillContent} name="content" />
            <ReactQuill theme="snow" value={quillContent} onChange={onChangeQuill} />
          </S.FormItem>
          {/* <Spacer />
          <InputDateTime
            required={true}
            name="open_reservation_at"
            defaultValue={info.open_reservation_at}
            label={I18n.t('generic.open_reservation_at')}
          />
          <Spacer />
          <InputDateTime
            required={true}
            name="close_reservation_at"
            defaultValue={info.close_reservation_at}
            label={I18n.t('generic.close_reservation_at')}
          /> */}
          {/* <Spacer />
          <CheckBox
            name="opened"
            defaultChecked={info.store_limited_flag}
            onChangeHandler={e => setOpenedChecked(e.target.checked)}
            label={I18n.t('generic.opened')}
          /> */}
        </>
      }
      handleSubmit={handleSubmit}
    />
  )
}

const S: { [key: string]: AnyStyledComponent } = {}
S.FormItem = styled.div`
  display: block;
  & + & {
    margin-top: 16px;
  }
`
S.FormComment = styled.div`
  font-size: 12px;
  padding-bottom: 16px;
  text-align: right;
  color: #777;
`
S.FormItemTitle = styled.p`
  width: 240px;
  margin-top: 8px;
  font-size: 15px;
  font-weight: bold;
  line-height: 1;

  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    width: 100%;
    margin-bottom: 12px;
  }
`
S.Footer = styled.div`
  display: flex;
  margin-top: 24px;
  justify-content: flex-start;
`
S.DropZoneWrapper = styled.div<{ hasImage: boolean }>`
  display: block;
  width: 100%;
  flex: 1;
  ${({ hasImage }) =>
    hasImage &&
    `
    display: none;
  `}
`
S.ButtonLeft = styled.div`
  margin-right: 20px;
`

S.ButtonSave = styled.div`
  button {
    width: 100%;
  }
`

S.ImageWrapper = styled.div<{ hasNoImage: boolean }>`
  display: flex;
  .DropZoneUploader {
    width: 100%;
    flex: 1;
    margin: 0;
  }

  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
`

S.ItemImage = styled.div`
  display: block;
  position: relative;
  width: 240px;
  height: inherit;
  background-color: rgb(244, 244, 244);
  border-radius: 3px;
  overflow: hidden;
  > img {
    width: inherit;
    height: inherit;
    object-fit: contain;
  }
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    width: 200px;
  }
`
S.NoImage = styled.p`
  width: 100%;
  height: 100%;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: #f4f4f4;
`
S.PreviewImage = styled.div`
  display: flex;
  flex: 1;
`
S.ImageButtons = styled.div`
  display: flex;
  flex: 1;
  max-width: 100px;
  text-align: center;
  margin-left: 50px;
  align-items: center;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 20px;
  }
`

S.FileSelectWrapper = styled.div`
  cursor: pointer;
  margin-right: 12px;
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    padding: 0 11px;
  }
  border-radius: 4px;
  outline: none;
  background-color: var(${THEME_COLOR_VARIABLE_NAME});
  color: #fff;
  border: none;

  &:hover {
    opacity: 0.6;
  }
  > a {
    color: #fff;
  }

  input {
    width: 0;
  }
`
export default AdminInfoEdit
